import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import SiteFooter from "@/components/pivotbox/SiteFooter";
import { trackLeadS0 } from "@/components/pivotbox/metaPixel";
import { gaGenerateLeadS0 } from "@/components/pivotbox/gaEvents";

const TYPE_COLOURS = {
  Aspirer:   { hex: "#5B2D8E", light: "#F0EBF8", grad: ["#3D1A6E", "#5B2D8E"] },
  Navigator: { hex: "#1E4D8C", light: "#E8EEF8", grad: ["#0f2660", "#1E4D8C"] },
  Evolver:   { hex: "#1B6B5A", light: "#E2F0EB", grad: ["#0a3328", "#1B6B5A"] },
  Seeker:    { hex: "#8B2A1A", light: "#FAEAE6", grad: ["#5a1010", "#8B2A1A"] },
};

export default function S0ReportPage() {
  const { reportId } = useParams();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [report, setReport] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const pdfTriggered = useRef(false);
  const analyticsFired = useRef(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Meta Pixel + GA4: S0 assessment completed — visitor landed on report.
  // Fires exactly once per report mount (StrictMode-safe via ref guard).
  useEffect(() => {
    if (!reportId || analyticsFired.current) return;
    analyticsFired.current = true;
    trackLeadS0();
    gaGenerateLeadS0();
  }, [reportId]);

  // Fetch full report content
  useEffect(() => {
    async function fetchContent() {
      try {
        const res = await fetch(`${API_URL}/api/s0-reports/${reportId}/content`);
        if (!res.ok) throw new Error("Report not found");
        const data = await res.json();
        setReport(data.report);
        if (data.pdf_url) setPdfUrl(data.pdf_url);
      } catch {
        setError("Could not load your report. Please try again.");
      } finally {
        setLoading(false);
      }
    }
    fetchContent();
  }, [reportId, API_URL]);

  // Auto-generate PDF in background for download
  useEffect(() => {
    if (!report || pdfUrl || pdfTriggered.current) return;
    pdfTriggered.current = true;
    setPdfGenerating(true);
    async function generatePdf() {
      try {
        const res = await fetch(`${API_URL}/api/s0-reports/${reportId}/generate-pdf`, { method: "POST" });
        const data = await res.json();
        if (data.success && data.pdf_url) setPdfUrl(data.pdf_url);
      } catch { /* silent */ }
      finally { setPdfGenerating(false); }
    }
    generatePdf();
  }, [report, pdfUrl, reportId, API_URL]);

  // Robust same-origin download: hit our backend proxy which streams the PDF back
  // with Content-Disposition: attachment. Avoids cross-origin <a download> issues
  // (Bunny CDN does not send CORS headers, so a direct fetch from the browser fails).
  const [downloading, setDownloading] = useState(false);
  const handleDownloadPdf = async () => {
    if (!pdfUrl || downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/s0-reports/${reportId}/download-pdf`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      // Extract filename from Content-Disposition header if present
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `Pivotbox_S0_Report_${reportId.slice(0, 8)}.pdf`;
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      console.error("PDF download failed:", err);
      // Fallback: open the Bunny URL in a new tab so the user can save manually
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!report) return;
    const dt = report.dominant_type;
    if (dt) localStorage.setItem("pb_archetype", dt.toLowerCase());
  }, [report]);

  if (loading) {
    return (
      <div className="s0-report-shell" data-testid="s0-report-loading">
        <div className="s0-report-container" style={{ textAlign: "center", padding: "4rem 1rem" }}>
          <div className="s0-report-generating__spinner" data-testid="s0-report-spinner" />
          <p className="s0-report-generating__text">Loading your report...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="s0-report-shell" data-testid="s0-report-no-data">
        <div className="s0-report-container">
          <div className="s0-report-empty">
            <h2>No report data found</h2>
            <p>{error || "Please take the Founder Signal Test first."}</p>
            <Link to="/foundersignaltest" className="pivotbox-button" data-testid="s0-report-back-to-test">Take the Test</Link>
          </div>
        </div>
      </div>
    );
  }

  const name = report.respondent_name;
  const dominantType = report.dominant_type;
  const dominantColour = report.dominant_colour;
  // v2 scoring: raw_scores is now decimal on /5 scale (e.g., 4.8)
  const dominantScore = report.raw_scores?.[dominantType] || 0;
  const dominantScoreStr = Number(dominantScore).toFixed(1);
  const dominantPct = Math.round((dominantScore / 5) * 100);
  const dominantDef = report.type_definitions?.[dominantType] || "";
  const ranked = report.ranked_scores;
  const rawScores = report.raw_scores;
  const typeDefs = report.type_definitions;
  const scoreDescs = report.score_descriptions || {};
  const sec01 = report.section_01_what_test_measures;
  const sec02 = report.section_02_dominant_headline;
  const sec04 = report.section_04_driving_you;            // { body: [...] }
  const sec05 = report.section_05_full_picture || [];     // [paragraphs]
  const strengths = report.section_strengths || [];
  const watchFors = report.section_watch_fors || [];
  const sec06 = report.section_06_blind_spot;             // { title, body }
  const sec07 = report.section_07_where_you_stand || [];  // [paragraphs]
  const sec08 = report.section_08_your_next_move || [];   // [paragraphs]
  const gradColours = TYPE_COLOURS[dominantType]?.grad || ["#0a3328", "#1B6B5A"];
  const scenarioType = report.scenario_type; // standard, all_balanced, two_way_tie, three_way_cluster, flat
  const isSpecialScenario = scenarioType && scenarioType !== "standard";

  return (
    <div className="s0-report-shell" data-testid="s0-report-page">
      {/* Header */}
      <header className="s0-report-header" data-testid="s0-report-header">
        <div className="s0-report-container s0-report-header__inner">
          <Link to="/" className="s0-report-logo" data-testid="s0-report-logo-link">
            <img src="/pivotbox-p-logo.png" alt="Pivotbox" className="s0-report-logo__mark" />
          </Link>
          <span className="s0-report-header__badge" data-testid="s0-report-header-badge">S0 Result</span>
        </div>
      </header>

      <main className="s0-report-main">
        <div className="s0-report-container">
          {/* Greeting */}
          <p className="s0-report-greeting" data-testid="s0-report-greeting">
            Hi <strong>{name || ""}</strong>,
          </p>
          <p className="s0-report-greeting-sub" data-testid="s0-report-greeting-sub">
            Here is what your Founder Signal Test reveals.
          </p>

          {/* ══ SEC 01: What This Test Measures ══ */}
          <section className="s0-html-section" data-testid="s0-section-01">
            <SectionBadge num="01" title="WHAT THIS TEST MEASURES" />
            <div className="s0-html-section__body">
              <h3 className="s0-html-section__heading">Understanding your result</h3>
              <p className="s0-html-section__text">{sec01}</p>
              <div className="s0-html-types" data-testid="s0-type-definitions">
                {Object.entries(typeDefs).map(([type, desc]) => (
                  <div className="s0-html-type-row" key={type}>
                    <span className="s0-html-type-dot" style={{ background: TYPE_COLOURS[type]?.hex }} />
                    <div>
                      <strong className="s0-html-type-name" style={{ color: TYPE_COLOURS[type]?.hex }}>{type}</strong>
                      <p className="s0-html-type-desc">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ══ SEC 02: Your Dominant Signal ══ */}
          <section className="s0-html-section s0-html-section--no-bg" data-testid="s0-section-02">
            <SectionBadge num="02" title="YOUR DOMINANT SIGNAL" />
            {isSpecialScenario ? (
              <>
                {/* Special scenario — no single dominant type */}
                <div className="s0-dom-card s0-dom-card--special" data-testid="s0-dominant-card">
                  <span className="s0-dom-card__eyebrow">YOUR FOUNDER SIGNAL PROFILE</span>
                  <h2 className="s0-dom-card__type-name">
                    {scenarioType === "all_balanced" && "Balanced Profile"}
                    {scenarioType === "two_way_tie" && `${ranked[0]?.type}-${ranked[1]?.type} Pair`}
                    {scenarioType === "three_way_cluster" && "Three-Way Cluster"}
                    {scenarioType === "flat" && "Flat Profile"}
                  </h2>
                  <p className="s0-dom-card__defn">
                    {scenarioType === "all_balanced" && "All four signals are broadly balanced — no single dominant force."}
                    {scenarioType === "two_way_tie" && `Two signals are co-dominant: ${ranked[0]?.type} and ${ranked[1]?.type}.`}
                    {scenarioType === "three_way_cluster" && `Three signals are closely clustered: ${ranked[0]?.type}, ${ranked[1]?.type}, and ${ranked[2]?.type}.`}
                    {scenarioType === "flat" && "All four signals are low — the motivational conditions are not yet strongly present."}
                  </p>
                  <div className="s0-dom-card__scores-mini">
                    {ranked.map((s) => (
                      <span key={s.type} className="s0-dom-card__score-chip" style={{ borderColor: s.colour, color: s.colour }}>
                        {s.type} {Number(s.score).toFixed(1)}/5
                      </span>
                    ))}
                  </div>
                </div>
                <div className="s0-dom-headline" data-testid="s0-dominant-headline">
                  <p>{sec02}</p>
                </div>
              </>
            ) : (
              <>
                {/* Standard scenario — single dominant type */}
                <div
                  className="s0-dom-card"
                  data-testid="s0-dominant-card"
                  style={{ background: `linear-gradient(135deg, ${gradColours[0]}, ${gradColours[1]})` }}
                >
                  <span className="s0-dom-card__eyebrow">YOUR FOUNDER SIGNAL TYPE</span>
                  <h2 className="s0-dom-card__type-name">The {dominantType}</h2>
                  <p className="s0-dom-card__defn">{dominantDef}</p>
                  <div className="s0-dom-card__score-row">
                    <span className="s0-dom-card__score-num">{dominantScoreStr}</span>
                    <span className="s0-dom-card__score-denom">/5</span>
                    <div className="s0-dom-card__bar-wrap">
                      <div className="s0-dom-card__bar-track">
                        <div className="s0-dom-card__bar-fill" style={{ width: `${dominantPct}%` }} />
                      </div>
                      <span className="s0-dom-card__bar-label">{dominantPct}% - your strongest signal</span>
                    </div>
                  </div>
                </div>
                <div className="s0-dom-headline" data-testid="s0-dominant-headline">
                  <p>{sec02}</p>
                </div>
              </>
            )}
          </section>

          {/* ══ SEC 03: Your Complete Score Profile ══ */}
          <section className="s0-html-section s0-html-section--no-bg" data-testid="s0-section-03">
            <SectionBadge num="03" title="YOUR COMPLETE SCORE PROFILE" />
            <div className="s0-html-scores-list" data-testid="s0-score-cards">
              {ranked.map((s) => (
                <div
                  className={`s0-score-card ${s.is_dominant ? "s0-score-card--dominant" : ""}`}
                  key={s.type}
                  style={s.is_dominant ? { background: TYPE_COLOURS[s.type]?.light } : {}}
                >
                  <div className="s0-score-card__top">
                    <span className="s0-score-card__type" style={{ color: s.colour }}>{s.type}</span>
                    <span className="s0-score-card__val" style={{ color: s.colour }}>{Number(s.score).toFixed(1)}<span className="s0-score-card__denom">/5</span></span>
                  </div>
                  <div className="s0-score-card__bar-track">
                    <div className="s0-score-card__bar-fill" style={{ width: `${s.pct}%`, background: s.colour }} />
                  </div>
                  <p className="s0-score-card__desc">{scoreDescs[s.type] || ""}</p>
                </div>
              ))}
            </div>
            {/* Radar chart */}
            <div className="s0-radar-card" data-testid="s0-radar-chart">
              <span className="s0-radar-card__title">SCORE PROFILE - ALL SIGNALS</span>
              <RadarSVG scores={rawScores} />
            </div>
          </section>

          {/* ══ SEC 04: What Is Driving You (paragraphs only) ══ */}
          <section className="s0-html-section" data-testid="s0-section-04">
            <SectionBadge num="04" title="WHAT IS DRIVING YOU" />
            <div className="s0-html-section__body">
              {sec04.body.map((para, i) => (
                <p className="s0-html-section__text" key={i}>{para}</p>
              ))}
            </div>
          </section>

          {/* ══ SEC 05: The Full Picture ══ */}
          <section className="s0-html-section" data-testid="s0-section-05">
            <SectionBadge num="05" title="THE FULL PICTURE" />
            <div className="s0-html-section__body">
              {sec05.map((para, i) => (
                <p className="s0-html-section__text" key={i}>{para}</p>
              ))}
            </div>
          </section>

          {/* ══ Strengths & Watch For (sub-block, no section number) ══ */}
          <section className="s0-html-section s0-html-section--no-bg" data-testid="s0-section-strengths-watch">
            <div className="s0-html-section__body">
              <div className="s0-trait-grid" data-testid="s0-trait-grid">
                <div className="s0-trait-col s0-trait-col--strength">
                  <span className="s0-trait-col__label">NATURAL STRENGTH</span>
                  {strengths.map((s, i) => (
                    <div className="s0-trait-item" key={i}>
                      <strong>{s.title}</strong>
                      <p>{s.body}</p>
                    </div>
                  ))}
                </div>
                <div className="s0-trait-col s0-trait-col--watch">
                  <span className="s0-trait-col__label">WATCH FOR</span>
                  {watchFors.map((w, i) => (
                    <div className="s0-trait-item" key={i}>
                      <strong>{w.title}</strong>
                      <p>{w.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ══ SEC 06: Your Blind Spot ══ */}
          <section className="s0-html-section s0-html-section--no-bg" data-testid="s0-section-06">
            <SectionBadge num="06" title="YOUR BLIND SPOT" />
            <div className="s0-blind-spot-card" data-testid="s0-blind-spot-card">
              <span className="s0-blind-spot-card__label">WHAT TO WATCH FOR</span>
              <h3 className="s0-blind-spot-card__title">{sec06.title}</h3>
              <p className="s0-blind-spot-card__body">{sec06.body}</p>
            </div>
          </section>

          {/* ══ SEC 07: Where You Stand ══ */}
          <section className="s0-html-section" data-testid="s0-section-07">
            <SectionBadge num="07" title="WHERE YOU STAND" />
            <div className="s0-html-section__body">
              {sec07.map((para, i) => (
                <p className="s0-html-section__text" key={i}>{para}</p>
              ))}
            </div>
          </section>

          {/* ══ SEC 08: Your Next Move ══ */}
          <section className="s0-html-section s0-html-section--no-bg" data-testid="s0-section-08">
            <SectionBadge num="08" title="YOUR NEXT MOVE" />
            <div className="s0-html-section__body">
              {sec08.map((para, i) => (
                <p className="s0-html-section__text" key={i}>{para}</p>
              ))}
            </div>
          </section>

          {/* ══ Download Button ══ */}
          <div className="s0-html-download" data-testid="s0-report-download-section">
            {pdfUrl ? (
              <button
                type="button"
                className="s0-html-download__btn"
                data-testid="s0-report-download-pdf"
                onClick={handleDownloadPdf}
                disabled={downloading}
              >
                {downloading ? "Downloading..." : "Download PDF Report"}
              </button>
            ) : pdfGenerating ? (
              <button className="s0-html-download__btn s0-html-download__btn--disabled" disabled data-testid="s0-report-pdf-generating">
                Preparing PDF...
              </button>
            ) : (
              <button className="s0-html-download__btn" data-testid="s0-report-generate-pdf-btn" onClick={() => { pdfTriggered.current = false; setPdfUrl(null); }}>
                Download PDF Report
              </button>
            )}
          </div>

          {/* CTA */}
          <div className="s0-report-incomplete" data-testid="s0-report-incomplete-box">
            <h2 className="s0-report-incomplete__headline" data-testid="s0-report-incomplete-headline">
              Knowing why you want to start<br /><em>is just the beginning.</em>
            </h2>
            <p className="s0-report-incomplete__body" data-testid="s0-report-incomplete-body">
              Before you make any moves, there are a few questions you need honest answers to.
            </p>
            <ul className="s0-report-incomplete__list" data-testid="s0-report-incomplete-list">
              <li>Can your current life actually support this move?</li>
              <li>What kind of founder are you?</li>
              <li>When things get uncertain and hard, how will you hold up?</li>
              <li>Where are your real strengths &mdash; and where are the gaps?</li>
              <li>Which kind of business actually fits the way you work?</li>
              <li>When is the right time to start?</li>
              <li>What should you try first?</li>
            </ul>
            <p className="s0-report-incomplete__body" data-testid="s0-report-incomplete-body-2">
              Most people start without knowing any of this.
            </p>
            <p className="s0-report-incomplete__body" data-testid="s0-report-incomplete-body-3">
              Pivotbox is built to help you find out &mdash; before you decide.
            </p>
            <div className="s0-report-incomplete__cta-wrap">
              <Link to="/find-out" state={{ archetype_label: dominantType?.toLowerCase(), combo_label: "" }} className="s0-report-incomplete__cta" data-testid="s0-report-incomplete-cta">
                Find out if you are ready &nbsp;&rarr;
              </Link>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

/* Section badge component */
function SectionBadge({ num, title }) {
  return (
    <div className="s0-badge-pill">
      <span className="s0-badge-pill__num">{num}</span>
      <span className="s0-badge-pill__title">{title}</span>
    </div>
  );
}

/* SVG Radar chart */
function RadarSVG({ scores }) {
  const cx = 170;
  const cy = 140;
  const R = 90;
  // viewBox is wider than tall to give the horizontal axis labels (Evolver / Aspirer) room
  const VBX = 0, VBY = 0, VBW = 340, VBH = 280;

  // 3-signal triangular radar. Seeker at top, Aspirer bottom-right, Evolver bottom-left.
  const axes = [
    { type: "Seeker",  angle: 270, col: "#8B2A1A" },  // top
    { type: "Aspirer", angle: 30,  col: "#5B2D8E" },  // bottom-right (SVG y grows down)
    { type: "Evolver", angle: 150, col: "#1B6B5A" },  // bottom-left
  ];

  const toRad = (deg) => (deg * Math.PI) / 180;
  const pts = axes.map((a) => {
    const s = Number(scores[a.type] || 0);
    // v2 scoring: signal_score is on /5 decimal scale
    const r = (s / 5) * R;
    return {
      ...a,
      score: s,
      x: cx + r * Math.cos(toRad(a.angle)),
      y: cy + r * Math.sin(toRad(a.angle)),
      lx: cx + (R + 36) * Math.cos(toRad(a.angle)),
      ly: cy + (R + 36) * Math.sin(toRad(a.angle)),
    };
  });

  const polygon = pts.map((p) => `${p.x},${p.y}`).join(" ");

  const gridCircles = [0.25, 0.5, 0.75, 1.0].map((f) => (
    <circle key={f} cx={cx} cy={cy} r={R * f} fill="none" stroke="#E4DDD2" strokeWidth="0.7" />
  ));

  // Draw a spoke to each vertex of the triangle
  const spokes = axes.map((a) => {
    const rad = toRad(a.angle);
    const ex = cx + R * Math.cos(rad);
    const ey = cy + R * Math.sin(rad);
    return <line key={a.type} x1={cx} y1={cy} x2={ex} y2={ey} stroke="#E4DDD2" strokeWidth="0.7" />;
  });

  return (
    <svg viewBox={`${VBX} ${VBY} ${VBW} ${VBH}`} className="s0-radar-svg" data-testid="s0-radar-svg">
      {gridCircles}
      {spokes}
      <polygon points={polygon} fill="#5B2D8E" fillOpacity="0.1" stroke="#5B2D8E" strokeWidth="2" />
      {pts.map((p) => (
        <g key={p.type}>
          <circle cx={p.x} cy={p.y} r="5" fill={p.col} stroke="white" strokeWidth="1.5" />
          <text x={p.lx} y={p.ly - 4} textAnchor="middle" fill={p.col} fontSize="11" fontWeight="700">{p.type}</text>
          <text x={p.lx} y={p.ly + 10} textAnchor="middle" fill="#7A7268" fontSize="9">{p.score.toFixed(1)}/5</text>
        </g>
      ))}
    </svg>
  );
}
